// ============================================================
// Dashboard — main entry point (replaces DashboardComponent)
// Route: /dashboard
//
// What this component does:
// 1. Redirects userType 75 to buspass-report
// 2. Loads two CloudFront CSVs (school/staff/students + enrollment)
// 3. Builds highlight cards (SharedHighlights) with drilldown
// 4. Shows "Live Data" and "Enrollment" tab toggle (hidden for userStatus users)
// 5. For userType 38/13: shows affiliated board multiselect filter
//
// Sub-routes are handled in DashboardRoutes.tsx (see below)
// ============================================================
import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import { RootState } from '../../store'; // adjust path
import SharedHighlights, { HighlightCard } from './shared/SharedHighlights';
import LiveData from './live-data/LiveData';
import ComparativeData from './comparative-data/ComparativeData';
import {
  parseCsvToObjects,
  sumField,
  safePct,
  getMngTypeOnUserType,
} from './shared/utils';
import districtJson from '../../assets/jsons/district.json';
import studentsIcon from '../../assets/images/Dashboard/totalStudent.svg';
import schoolsIcon from '../../assets/images/Dashboard/totalSchool.svg';
import staffsIcon from '../../assets/images/Dashboard/totalStaff.svg';
// Affiliated board options (for DEO users type 38/13)
const BOARD_OPTIONS = [
  { name: 'CBSE', code: '1' },
  { name: 'ICSE', code: '2' },
  { name: 'GCSE', code: '3' },
  { name: 'International Board', code: '4' },
  { name: 'State Board', code: '5' },
  { name: 'Other Board', code: '6' },
];

type ActiveTab = 'livedata' | 'enrollment' | null;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const s = useSelector((state: RootState) => state.session);
  const usertypeid = s.usertypeid ?? '';
  const emisUsertype1 = s.emisUsertype1 ?? '';
  const districtId = String(s.districtId ?? '');
  const blockId = String(s.blockId ?? '');
  const edu_district_id = String(s.edu_district_id ?? '');
  // Redirect bus-pass user
  useEffect(() => {
    if (usertypeid === '75') navigate('/component/reports/buspass-report');
  }, [usertypeid, navigate]);

  // userStatus: hide tabs when userType=5 AND userType1=24
  const userStatus = usertypeid === '5' && emisUsertype1 === '24';

  // Active tab
  const [activeTab, setActiveTab] = useState<ActiveTab>('livedata');

  // Affiliated board multiselect (DEO users type 38 / 13)
  const isDeO = usertypeid === '38' || usertypeid === '13';
  const [affiliatedBoard, setAffiliatedBoard] = useState<string[]>(
    BOARD_OPTIONS.map((b) => b.code)
  );

  // Raw CSV data
  const [masterData, setMasterData] = useState<any[]>([]); // school/staff/students
  const [masterData1, setMasterData1] = useState<any[]>([]); // enrollment
  const [districtList, setDistrictList] = useState<any[]>([]);

  // Processed output
  const [highLights, setHighLights] = useState<HighlightCard[]>([]);
  const [dataReady, setDataReady] = useState(false);

  // ---- Load data ----
  useEffect(() => {
    const csvUrl =
      usertypeid === '38' || usertypeid === '13'
        ? 'https://d3ijhv7dn0xr3b.cloudfront.net/10238.csv'
        : 'https://d3ijhv7dn0xr3b.cloudfront.net/10225.csv';

    const enrolUrl = 'https://d3ijhv7dn0xr3b.cloudfront.net/10550.csv';

    const parseCsv = (url: string) =>
      new Promise<any[]>((resolve) => {
        Papa.parse(url, {
          download: true,
          complete: (r) => resolve(parseCsvToObjects(r.data as string[][])),
          error: () => resolve([]),
        });
      });

    Promise.all([
      Promise.resolve(districtJson),
      parseCsv(csvUrl),
      parseCsv(enrolUrl),
    ]).then(([districts, md, md1]) => {
      setDistrictList(districts);

      // Filter private school logins
      const filterPrivate = (rows: any[]) =>
        usertypeid === '24' && ['5', '6', '1', '2', '3'].includes(emisUsertype1)
          ? rows.filter((x) => String(x.manage_id) === '2')
          : rows;

      setMasterData(filterPrivate(md));
      setMasterData1(filterPrivate(md1));
    });
  }, [usertypeid, emisUsertype1]);

  const buildHighlights = useCallback(
    (md: any[], md1: any[]) => {
      // Determine geo filter
      const geoType = getMngTypeOnUserType(usertypeid, emisUsertype1);
      let filtered = md;
      let filtered1 = md1;

      const applyGeo = (rows: any[]) => {
        if (typeof geoType === 'number' || Array.isArray(geoType)) {
          const ids = ([] as number[]).concat(geoType as any).map(String);
          return rows.filter((x) => ids.includes(String(x.manage_id)));
        }
        if (geoType === 'district')
          return rows.filter((x) => String(x.district_id) === districtId);
        if (geoType === 'edu_district')
          return rows.filter((x) => String(x.edu_dist_id) === edu_district_id);
        if (geoType === 'block')
          return rows.filter((x) => String(x.block_id) === blockId);
        return rows;
      };

      filtered = applyGeo(md);
      filtered1 = applyGeo(md1);
      // ---- Students card ----
      const totalBoys = sumField(filtered, 'total_b');
      const totalGirls = sumField(filtered, 'total_g');
      const totalStudents = sumField(filtered, 'total');
      const lastDate = filtered[0]?.Current_date ?? '';

      // Total Schools
      const government = filtered.filter((y) => y.school_type_id == '1');
      const Others = filtered.filter((y) => y.school_type_id != '1');

      // ---- Schools card ----
      const totalGovernment = sumField(government, 'school_count');
      const totalOthers = sumField(Others, 'school_count');
      const totalSchools = sumField(filtered, 'school_count');

      // ---- Staff card ----
      const totalTeaching = sumField(filtered, 'tot_teaching_staff');
      const totalNonTeaching = sumField(filtered, 'tot_non_teaching_staff');
      const totalStaffs = sumField(filtered, 'total_staffs');

      // ---- Enrollment card ----
      const totalFresh = sumField(filtered1, 'fresh');
      const totalCommon = sumField(filtered1, 'existing');
      const totalEnrolment = totalFresh + totalCommon;

      const cards: HighlightCard[] = [
        {
          heading: 'Total Students',
          headingValue: totalStudents,
          headingDate: lastDate,
          icon: studentsIcon,
          color: '#0b7db1',
          subMenuItems: [
            {
              SubHeading: 'Boys',
              SubHeadingValue: totalBoys,
              SubHeadingValuePer: safePct(totalBoys, totalStudents, 1),
            },
            {
              SubHeading: 'Girls',
              SubHeadingValue: totalGirls,
              SubHeadingValuePer: safePct(totalGirls, totalStudents, 1),
            },
          ],
        },
        {
          heading: 'Total Schools',
          headingValue: totalSchools,
          icon: schoolsIcon,
          headingDate: lastDate,
          color: '#73b829',
          subMenuItems: [
            {
              SubHeading: 'Government',
              SubHeadingValue: totalGovernment,
              SubHeadingValuePer: safePct(totalGovernment, totalSchools, 1),
            },
            {
              SubHeading: 'Others',
              SubHeadingValue: totalOthers,
              SubHeadingValuePer: safePct(totalOthers, totalSchools, 1),
            },
          ],
        },
        {
          heading: 'Total Staff',
          headingValue: totalStaffs,
          color: '#7c86ff',
          headingDate: lastDate,
          icon: staffsIcon,
          subMenuItems: [
            {
              SubHeading: 'Teaching',
              SubHeadingValue: totalTeaching,
              SubHeadingValuePer: safePct(totalTeaching, totalStaffs, 1),
            },
            {
              SubHeading: 'Non Teaching',
              SubHeadingValue: totalNonTeaching,
              SubHeadingValuePer: safePct(totalNonTeaching, totalStaffs, 1),
            },
          ],
        },
        // {
        //   heading: 'Enrollment',
        //   headingValue: totalEnrolment,
        //   color: '#ffb84c',
        //   icon: enrollmentIcon,
        //   subMenuItems: [
        //     {
        //       SubHeading: 'Fresh Profile',
        //       SubHeadingValue: totalFresh,
        //       SubHeadingValuePer: safePct(totalFresh, totalEnrolment, 1),
        //     },
        //     {
        //       SubHeading: 'Common Pool',
        //       SubHeadingValue: totalCommon,
        //       SubHeadingValuePer: safePct(totalCommon, totalEnrolment, 1),
        //     },
        //   ],
        // },
      ];

      setHighLights(cards);
    },
    [usertypeid, emisUsertype1, districtId, edu_district_id, blockId]
  );

  // ---- Build highlight cards when both CSVs and districtList are loaded ----
  useEffect(() => {
    if (!masterData.length || !masterData1.length || !districtList.length)
      return;
    queueMicrotask(() => {
      buildHighlights(masterData, masterData1);
      setDataReady(true);
    });
  }, [masterData, masterData1, districtList, affiliatedBoard, buildHighlights]);

  // When affiliated board changes for DEO users, re-filter
  useEffect(() => {
    if (!masterData.length || !masterData1.length) return;
    if (isDeO) {
      const filtered = masterData.filter((x) =>
        affiliatedBoard.includes(String(x.board_id))
      );
      queueMicrotask(() => buildHighlights(filtered, masterData1));
    }
  }, [affiliatedBoard, isDeO, masterData, masterData1, buildHighlights]);

  return (
    <div className="dashboard-container">
      {/* Affiliated board filter — DEO users only */}
      {isDeO && (
        <div className="col-md-3 col-12 mb-3">
          <b>
            <label>AFFILIATED BOARD</label>
          </b>
          <select
            multiple
            className="form-select"
            value={affiliatedBoard}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions).map(
                (o) => o.value
              );
              setAffiliatedBoard(selected);
            }}
          >
            {BOARD_OPTIONS.map((opt) => (
              <option key={opt.code} value={opt.code}>
                {opt.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Highlight cards */}
      {dataReady && <SharedHighlights highLights={highLights} />}

      {/* Tab toggle — hidden for userStatus users */}
      {!userStatus && (
        <div className="row mb-3">
          <div className="col-12">
            <div className="card" style={{ borderRadius: 5 }}>
              <div className="btn-group btn-group-lg" role="group">
                <button
                  type="button"
                  className={`btn ${activeTab === 'livedata' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() =>
                    setActiveTab(activeTab === 'livedata' ? null : 'livedata')
                  }
                >
                  <b>Live Data</b>
                </button>
                <button
                  type="button"
                  className={`btn ${activeTab === 'enrollment' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() =>
                    setActiveTab(
                      activeTab === 'enrollment' ? null : 'enrollment'
                    )
                  }
                >
                  <b>Enrollment</b>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Live Data tab content */}
      {activeTab === 'livedata' && !userStatus && <LiveData />}

      {/* Comparative/Enrollment tab content */}
      {activeTab === 'enrollment' && !userStatus && <ComparativeData />}
    </div>
  );
};

export default Dashboard;
